import React, { useState, useEffect } from 'react';
import { Modal, Banner, FormField } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import {
  ICOverride, ICOverrideCreate, ICOverrideUpdate, PortfolioOption, UserOption,
} from '../../../types/admin';

interface ICOverrideDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  editingOverride?: ICOverride | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ICOverrideDialog: React.FC<ICOverrideDialogProps> = ({
  open, mode, editingOverride, onClose, onSuccess,
}) => {
  const [formData, setFormData] = useState<ICOverrideCreate>({
    portfolio_id_lig: '', override_ic_user_id: 0, reason: '',
    effective_date: new Date().toISOString().split('T')[0],
  });
  const [portfolios, setPortfolios] = useState<PortfolioOption[]>([]);
  const [icUsers, setICUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valErrors, setValErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      fetchData();
      if (mode === 'edit' && editingOverride) {
        setFormData({
          portfolio_id_lig: editingOverride.portfolio_id_lig,
          override_ic_user_id: editingOverride.override_ic_user_id,
          reason: editingOverride.reason,
          effective_date: editingOverride.effective_date.split('T')[0],
        });
      } else {
        setFormData({
          portfolio_id_lig: '', override_ic_user_id: 0, reason: '',
          effective_date: new Date().toISOString().split('T')[0],
        });
      }
      setValErrors({}); setError(null);
    }
  }, [open, mode, editingOverride]);

  const fetchData = async () => {
    setFetchingData(true);
    try {
      const [pData, icData] = await Promise.all([
        apiService.getAdminPortfolios(),
        apiService.getICUsers(),
      ]);
      setPortfolios(pData); setICUsers(icData);
      if (!pData.length) setError('No portfolios found.');
      else if (!icData.length) setError('No IC users found. Please create users with user_type="IC" first.');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally { setFetchingData(false); }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.portfolio_id_lig) e.portfolio_id_lig = 'Portfolio is required';
    if (!formData.override_ic_user_id) e.override_ic_user_id = 'IC User is required';
    if (!formData.reason?.trim()) e.reason = 'Reason is required';
    else if (formData.reason.length > 1000) e.reason = 'Reason must be less than 1000 characters';
    if (!formData.effective_date) e.effective_date = 'Effective date is required';
    setValErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError(null);
    try {
      if (mode === 'create') {
        await apiService.createICOverride(formData);
      } else if (editingOverride) {
        const update: ICOverrideUpdate = {};
        if (formData.override_ic_user_id !== editingOverride.override_ic_user_id)
          update.override_ic_user_id = formData.override_ic_user_id;
        if (formData.reason !== editingOverride.reason) update.reason = formData.reason;
        if (formData.effective_date !== editingOverride.effective_date.split('T')[0])
          update.effective_date = formData.effective_date;
        if (Object.keys(update).length) await apiService.updateICOverride(editingOverride.id, update);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || `Failed to ${mode} IC override`);
    } finally { setLoading(false); }
  };

  const handleCancel = () => { setValErrors({}); setError(null); onClose(); };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={mode === 'create' ? 'Create IC Override' : 'Edit IC Override'}
      size="lg"
      footer={
        <>
          <LCBtn variant="outline" size="sm" onClick={handleCancel} disabled={loading}>Cancel</LCBtn>
          <LCBtn variant="primary" size="sm" onClick={handleSubmit} disabled={loading || fetchingData}>
            {loading
              ? (mode === 'create' ? 'Creating…' : 'Updating…')
              : (mode === 'create' ? 'Create Override' : 'Update Override')}
          </LCBtn>
        </>
      }
    >
      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-4">{error}</Banner>}
      {fetchingData ? (
        <div className="flex justify-center py-6 text-[#7A7A76] text-[13px]">Loading data…</div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <FormField label="Portfolio" required error={valErrors.portfolio_id_lig}
            hint={mode === 'edit' ? 'Portfolio cannot be changed in edit mode' : 'Select the portfolio to override IC for'}>
            <select
              className={CLS.input}
              value={formData.portfolio_id_lig}
              onChange={e => setFormData(p => ({ ...p, portfolio_id_lig: e.target.value }))}
              disabled={loading || mode === 'edit'}
            >
              <option value="">— Select Portfolio —</option>
              {portfolios.map(p => (
                <option key={p.portfolio_id_lig} value={p.portfolio_id_lig}>{p.label}</option>
              ))}
            </select>
          </FormField>

          <FormField label="IC User" required error={valErrors.override_ic_user_id}
            hint="Select the IC user to override with">
            <select
              className={CLS.input}
              value={formData.override_ic_user_id || ''}
              onChange={e => setFormData(p => ({ ...p, override_ic_user_id: Number(e.target.value) }))}
              disabled={loading}
            >
              <option value="">— Select IC User —</option>
              {icUsers.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </FormField>

          <FormField label="Reason" required error={valErrors.reason}
            hint={`Required for audit trail (${formData.reason.length}/1000 characters)`}>
            <textarea
              className={`${CLS.input} min-h-[100px] resize-y`}
              value={formData.reason}
              onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))}
              disabled={loading}
              maxLength={1000}
            />
          </FormField>

          <FormField label="Effective Date" required error={valErrors.effective_date}
            hint="Date when override takes effect">
            <input
              className={CLS.input}
              type="date"
              value={formData.effective_date}
              onChange={e => setFormData(p => ({ ...p, effective_date: e.target.value }))}
              disabled={loading}
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
};

export default ICOverrideDialog;
